import type { AdminGameVersion, InstallStep, UpdateGameVersionRequest } from '@mcgames/contracts';

export type StepType = InstallStep['type'];

export const STEP_TYPES: readonly StepType[] = [
  'extract',
  'extract_iso',
  'run_installer',
  'copy',
  'redist',
  'set_exe',
];

/**
 * One install step as the form edits it. Every step type uses the subset of fields it needs; the
 * numbers and the argument list stay text until they are saved.
 */
export interface StepDraft {
  /** Stable identity for React keys across moves and removals. */
  key: number;
  type: StepType;
  path: string;
  from: string;
  to: string;
  /** One argument per line. */
  args: string;
  timeoutMinutes: string;
}

export interface InstallDraft {
  steps: StepDraft[];
  archivePassword: string;
  exe: string;
}

export type InstallRequest = Required<
  Pick<UpdateGameVersionRequest, 'installSteps' | 'archivePassword' | 'exe'>
>;

let nextKey = 1;

export function newStepDraft(type: StepType): StepDraft {
  return { key: nextKey++, type, path: '', from: '', to: '', args: '', timeoutMinutes: '' };
}

export function stepToDraft(step: InstallStep): StepDraft {
  const draft = newStepDraft(step.type);
  switch (step.type) {
    case 'extract':
      return draft;
    case 'extract_iso':
    case 'set_exe':
      return { ...draft, path: step.path };
    case 'run_installer':
      return {
        ...draft,
        path: step.path,
        args: step.args.join('\n'),
        timeoutMinutes: step.timeoutMinutes?.toString() ?? '',
      };
    case 'redist':
      return { ...draft, path: step.path, args: step.args.join('\n') };
    case 'copy':
      return { ...draft, from: step.from, to: step.to };
  }
}

/** Trims every line and drops the empty ones. */
export function parseArgs(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/** The step without fields its type does not take: the API rejects extra properties. */
export function draftToStep(draft: StepDraft): InstallStep {
  switch (draft.type) {
    case 'extract':
      return { type: 'extract' };
    case 'extract_iso':
      return { type: 'extract_iso', path: draft.path.trim() };
    case 'set_exe':
      return { type: 'set_exe', path: draft.path.trim() };
    case 'run_installer': {
      const step: InstallStep = {
        type: 'run_installer',
        path: draft.path.trim(),
        args: parseArgs(draft.args),
      };
      if (draft.timeoutMinutes.trim() !== '') step.timeoutMinutes = Number(draft.timeoutMinutes);
      return step;
    }
    case 'redist':
      return { type: 'redist', path: draft.path.trim(), args: parseArgs(draft.args) };
    case 'copy':
      return { type: 'copy', from: draft.from.trim(), to: draft.to.trim() };
  }
}

export function installDraftFromVersion(version: AdminGameVersion): InstallDraft {
  return {
    steps: version.installSteps.map(stepToDraft),
    archivePassword: version.archivePassword ?? '',
    exe: version.exe ?? '',
  };
}

export function installRequest(draft: InstallDraft): InstallRequest {
  const exe = draft.exe.trim();
  return {
    installSteps: draft.steps.map(draftToStep),
    archivePassword: draft.archivePassword === '' ? null : draft.archivePassword,
    exe: exe === '' ? null : exe,
  };
}
